// frontend/src/components/TradeDesk.tsx
//
// The day-trader desk. One screen answers three questions, in order:
//   1. Is there anything to trade RIGHT NOW?  → "Ready" cards with a full plan
//   2. What is each coin waiting for?          → "Watching" checklist rows
//   3. How does an entry happen here at all?   → the funnel strip + Playbook link
//
// Layout (pro terminal, Kraken Pro / Coinbase Advanced style):
//   market header strip → compact 24h session bar →
//   [desk symbol list | chart | risk guard] → tabbed desk panel
//   (Ready · Watching · Intraday levels, plus "How an entry happens").
//
// Data comes from /api/trading-signals/desk, which runs the exact validated
// pipeline the Telegram scanner and backtester use (1h entries, 4h bias).

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Key } from "react-aria-components";
import {
  RefreshCw, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Clock,
  CheckCircle2, Circle, XCircle, BookOpen, ShieldAlert, Target, Info, Radar,
} from "lucide-react";
import {
  Alert, Button, Chip, Label, ProgressBar, Skeleton, Table, Tabs, Tooltip, buttonVariants,
} from "@heroui/react";
import { DirectionChip, EmptyState, Panel } from "@/components/kit";
import { cn } from "@/lib/utils";
import {
  OPEN_SIGNAL_META, STAGE_META, VERDICT_TONE, ago, archetypeLabel, chartSignalFromTrade, deskCheckFor, deskSetupFor, fmt, pct,
  ruleCounts, useDesk, useMyTrades,
  type DeskRow, type OpenSignal, type Stage, type SummaryPeriod, type Universe, type Verdict,
} from "./desk/deskData";
import { TradeTicket, type TicketRequest } from "./desk/TradeTicket";
import { TodayTrades } from "./desk/TodayTrades";
import type { ChartSignal } from "./chart/chartSignals";
import { AgainstBtcChip, ClosePreviewFor, Mini, PriceLadder, RuleHelp, StageChip, StatusIcon, VerdictBox } from "./desk/parts";
import { SessionClock } from "./daytrader/SessionClock";
import { RiskGuard } from "./daytrader/RiskGuard";
import { useDayRiskStatus } from "../hooks/useDayRisk";
import { IntradayLevels, useIntradayLevels } from "./daytrader/IntradayLevels";
import { SESSION_META, chartLevelsFrom } from "./daytrader/dayTrader";
import { PriceChart } from "./chart/PriceChart";
import { CoinPicker, FavoriteStar, MarketList } from "./desk/MarketList";
import { DEFAULT_SYMBOL, changeText, symbolFromToken, tokenFromSymbol } from "./desk/coinList";
import { useTickers } from "../hooks/useTickers";
import type { Timeframe } from "../types";

// 5m / 15m zoom in for timing, 1h is where the engine decides, 4h / 1D zoom
// out for the bigger trend.
const CHART_TIMEFRAMES: Timeframe[] = ["5m", "15m", "1h", "4h", "1d"];

// ─── page ────────────────────────────────────────────────────────────────────

export function TradeDesk() {
  const { coins, universe, data, loading, error, scan, countdown, logTrade, logging, toast, ready, watching, firstLoad, openSignals } = useDesk();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showHow, setShowHow] = useState<boolean>(() => {
    try { return localStorage.getItem("vknow_desk_how") !== "hidden"; } catch { return true; }
  });

  // The coin, timeframe and full screen live in the address bar
  // (/?token=SOL&tf=4h&fullscreen=1), so a refresh or a shared link opens the same view.
  const [params, setParams] = useSearchParams();
  const setParam = useCallback((key: string, value: string | null) => {
    setParams(p => {
      const next = new URLSearchParams(p);
      if (value === null) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setParams]);
  const chartSymbol = symbolFromToken(params.get("token")) ?? DEFAULT_SYMBOL;
  const setChartSymbol = useCallback((symbol: string) => setParam("token", tokenFromSymbol(symbol)), [setParam]);
  const fullScreen = params.get("fullscreen") === "1" || params.get("fullscreen") === "true";
  const setFullScreen = useCallback((on: boolean) => setParam("fullscreen", on ? "1" : null), [setParam]);
  // Opens on 1h: the engine decides on closed 1h candles (setup, trigger,
  // stop, targets). The other timeframes zoom in or out around it.
  const chartTf: Timeframe = CHART_TIMEFRAMES.find(tf => tf === params.get("tf")?.toLowerCase()) ?? "1h";
  const setChartTf = useCallback((tf: Timeframe) => setParam("tf", tf), [setParam]);
  const [showLevels, setShowLevels] = useState(true);
  const [showSignals, setShowSignals] = useState(true);
  const intraday = useIntradayLevels(chartSymbol);
  const chartLevels = useMemo(
    () => (showLevels ? chartLevelsFrom(intraday.data?.distances) : []),
    [showLevels, intraday.data],
  );
  // Presentation only: which tab of the desk panel is open.
  const [tab, setTab] = useState<Key>("ready");

  // The trader's own trades: Buy/Sell ticket, results panel, and on the chart.
  const [ticket, setTicket] = useState<TicketRequest | null>(null);
const [ticketToast, setTicketToast] = useState<string | null>(null);
  const [tradesPeriod, setTradesPeriod] = useState<SummaryPeriod>("today");
  const my = useMyTrades(tradesPeriod);
  const chartTrades = useMemo(
    () => my.trades.filter(t => t.symbol === chartSymbol).map(chartSignalFromTrade).filter((s): s is ChartSignal => s !== null),
    [my.trades, chartSymbol],
  );
  useEffect(() => {
    if (!ticketToast) return;
    const id = setTimeout(() => setTicketToast(null), 3000);
    return () => clearTimeout(id);
  }, [ticketToast]);

  const toggleHow = () => {
    setShowHow(v => {
      try { localStorage.setItem("vknow_desk_how", v ? "hidden" : "shown"); } catch { /* ignore */ }
      return !v;
    });
  };

  // The one risk-per-trade figure (Settings › Account): the safety limit, the
  // ticket and every plan on this page size from it.
  const { status: dayRisk } = useDayRiskStatus();
  const riskAmount = dayRisk?.riskPerTradeUsd ?? 0;

  // ── presentation-only derivations ────────────────────────────────────────
  const rowBySymbol = useMemo(() => {
    const m = new Map<string, DeskRow>();
    for (const r of watching) m.set(r.symbol, r);
    for (const r of ready) m.set(r.symbol, r);
    return m;
  }, [ready, watching]);
  const readySet = useMemo(() => new Set(ready.map(r => r.symbol)), [ready]);
  // Every USDT coin's price, 24h change and volume: the list, search and favourites.
  const tickers = useTickers();
  const ticker = tickers?.get(chartSymbol) ?? null;
  // A coin Binance doesn't trade (a typo in the link, or delisted): back to BTC.
  useEffect(() => {
    if (tickers && tickers.size > 0 && !tickers.has(chartSymbol)) setChartSymbol(DEFAULT_SYMBOL);
  }, [tickers, chartSymbol, setChartSymbol]);
  const coinListProps = {
    coins, universe, rowBySymbol, readySet, tickers, selected: chartSymbol, onSelect: setChartSymbol, loading: firstLoad,
  };
  const selectedRow = rowBySymbol.get(chartSymbol) ?? null;
  // Levels for the coin on the chart only: just after a switch they still hold the last coin's.
  const lv = intraday.data?.symbol === chartSymbol ? intraday.data : null;
  const lastPrice = lv?.lastPrice ?? selectedRow?.price ?? null;
  const vwapDist = lv?.distances.find(d => d.key === "vwap") ?? null;
  // Header cells show a skeleton only while levels are still coming in.
  const lvPending = !lv && !intraday.error;
  const dash = <span className="num text-sm text-muted">—</span>;

  return (
    <div className="flex flex-col gap-3">
      {/* ── Market header strip ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-stretch gap-x-5 gap-y-3 rounded-lg border border-border bg-surface px-4 py-3">
        <div className="-ml-1.5 flex min-w-0 items-center gap-1">
          <FavoriteStar symbol={chartSymbol} />
          <div className="flex min-w-0 flex-col justify-center">
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold tracking-tight">{chartSymbol.replace("USDT", "")}</span>
            <span className="text-xs text-muted">/ USDT</span>
          </div>
          <span className="text-2xs text-muted">
            {data ? `${data.config.signalTf.toUpperCase()} entries · ${data.config.biasTf.toUpperCase()} bias · ${data.scanned} coins` : "Loading desk…"}
          </span>
          </div>
        </div>

        <HeaderStat label="Last price">
          {lastPrice == null ? (lvPending ? <Skeleton className="h-5 w-24 rounded-md" /> : dash) : (
            <span className="flex items-baseline gap-2">
              <span className={cn("num text-base font-semibold", ticker && (ticker.changePct >= 0 ? "text-profit" : "text-loss"))}>{fmt(lastPrice)}</span>
              {ticker && (
                <span className={cn("num text-xs", ticker.changePct >= 0 ? "text-profit" : "text-loss")} title="Change over the last 24 hours">
                  {changeText(ticker.changePct)} 24h
                </span>
              )}
            </span>
          )}
        </HeaderStat>

        <HeaderStat
          label="VWAP dist"
          hint={vwapDist ? `Daily VWAP ${fmt(vwapDist.price)} is ${Math.abs(vwapDist.pct).toFixed(2)}% ${vwapDist.pct > 0 ? "above" : "below"} price` : "Distance from price to today's VWAP"}
        >
          {!lv ? (lvPending ? <Skeleton className="h-5 w-20 rounded-md" /> : dash) : vwapDist ? (
            <span className="num text-sm">
              {pct(vwapDist.pct)}
              {vwapDist.atr != null && <span className="ml-1.5 text-xs text-muted">{vwapDist.atr > 0 ? "+" : ""}{vwapDist.atr.toFixed(1)} ATR</span>}
            </span>
          ) : <span className="num text-sm text-muted">—</span>}
        </HeaderStat>

        <HeaderStat label={lv ? `ATR ${lv.atr.tf}(${lv.atr.period})` : "ATR"}>
          {!lv ? (lvPending ? <Skeleton className="h-5 w-16 rounded-md" /> : dash) : <span className="num text-sm">{fmt(lv.atr.value)}</span>}
        </HeaderStat>

        <HeaderStat label="Session">
          {!lv ? (lvPending ? <Skeleton className="h-5 w-16 rounded-md" /> : dash) : (
            <Chip size="sm" variant="soft" className={cn("font-semibold", SESSION_META[lv.levels.session].text)}>
              {SESSION_META[lv.levels.session].label}
            </Chip>
          )}
        </HeaderStat>

        <HeaderStat label="Next 1h close" hint="Signals are evaluated on CLOSED 1h candles. Nothing new can appear until the next close.">
          <span className="num flex items-center gap-1.5 text-sm">
            <Clock className="size-3.5 text-muted" aria-hidden /> {data ? countdown.label : "—"}
          </span>
        </HeaderStat>

        {data ? (
          <>
            <HeaderStat label="BTC 4h bias" hint="Direction of the market leader on the bias timeframe">
              <BiasText value={data.market.btcBias4h} up="bullish" down="bearish" />
            </HeaderStat>
            <HeaderStat label="BTC gate" hint="When BTC trends hard, altcoin signals against it go out at half risk">
              <BiasText value={data.market.btcTrend} up="BULLISH" down="BEARISH" />
            </HeaderStat>
            <HeaderStat label="Macro" hint={data.market.macro?.summary || "Macro sentiment"}>
              <BiasText
                value={data.market.macro ? data.market.macro.sentiment.replace("_", " ") : "—"}
                up="RISK ON"
                down="RISK OFF"
              />
            </HeaderStat>
          </>
        ) : firstLoad ? (
          <div className="flex items-center gap-6">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="space-y-1.5"><Skeleton className="h-2.5 w-14 rounded" /><Skeleton className="h-4 w-16 rounded" /></div>
            ))}
          </div>
        ) : null}

        <HeaderStat label="Risk / trade" hint="Change in Settings › Account. Every plan below is sized so a stop-out loses exactly this.">
          <span className="num text-sm">
            {dayRisk ? `$${riskAmount.toFixed(2)}` : "—"}
            {dayRisk && <span className="ml-1.5 text-xs text-muted">{dayRisk.config.riskPerTradePct}% of ${dayRisk.config.accountSize}</span>}
          </span>
        </HeaderStat>

        <div className="ml-auto flex items-center gap-2 self-center">
          <Link to="/playbook" className={buttonVariants({ variant: "ghost", size: "sm" })} aria-label="Playbook" title="Playbook: how each setup works">
            <BookOpen className="size-3.5" /> <span className="hidden 2xl:inline">Playbook</span>
          </Link>
          <Button size="sm" variant="primary" onPress={scan} isDisabled={loading}>
            <RefreshCw className={cn("size-3.5", loading && "animate-spin")} /> Scan
          </Button>
        </div>
      </div>

      {/* ── Compact 24h session bar ─────────────────────────────────────── */}
      <SessionClock />

      {error && (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Desk scan failed</Alert.Title>
            <Alert.Description>{error}</Alert.Description>
          </Alert.Content>
          <Button size="sm" variant="secondary" onPress={scan} isDisabled={loading}>Retry</Button>
        </Alert>
      )}

      {/* ── Terminal grid: symbols | chart | risk ───────────────────────── */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)_320px]">
        {/* Coin list (Binance's markets panel). On wide screens it fills the
            row the chart sets (absolute: its rows never make the row taller). */}
        <div className="relative order-2 lg:order-none">
          <MarketList className="h-[460px] lg:absolute lg:inset-0 lg:h-auto" {...coinListProps} />
        </div>

        <PriceChart
          className="order-1 min-w-0 lg:order-none"
          symbol={chartSymbol}
          timeframe={chartTf}
          expanded={fullScreen}
          onExpandedChange={setFullScreen}
          levels={chartLevels}
          showSignals={showSignals}
          deskCheck={deskCheckFor(selectedRow)}
          setup={deskSetupFor(selectedRow)}
          myTrades={chartTrades}
          riskUsd={riskAmount}
          timeframes={CHART_TIMEFRAMES}
          onTimeframe={setChartTf}
          onShowSignals={setShowSignals}
          showLevels={showLevels}
          onShowLevels={setShowLevels}
          symbolControl={<CoinPicker {...coinListProps} />}
          onBuy={() => setTicket({ symbol: chartSymbol, side: "LONG" })}
          onSell={() => setTicket({ symbol: chartSymbol, side: "SHORT" })}
        />

        <div className="order-3 flex min-w-0 flex-col gap-3 lg:order-none lg:col-span-2 xl:col-span-1">
          <TodayTrades my={my} period={tradesPeriod} onPeriod={setTradesPeriod} riskUsd={riskAmount} />
          <RiskGuard lastPrice={intraday.data?.lastPrice ?? null} symbol={chartSymbol} />
        </div>
      </div>

      <TradeTicket
        request={ticket}
        price={lastPrice}
        row={selectedRow}
        openSignal={openSignals.find(o => o.symbol === chartSymbol) ?? null}
        atr={selectedRow?.checklist.levels.atr ?? lv?.atr.value ?? null}
        onClose={() => setTicket(null)}
        onTracked={setTicketToast}
      />

      {/* ── Desk panel: tabbed content ──────────────────────────────────── */}
      <Panel flush>
        <Tabs variant="secondary" selectedKey={String(tab)} onSelectionChange={setTab}>
          <div className="flex flex-wrap items-center gap-2 border-b border-separator pr-3">
            <Tabs.ListContainer className="min-w-0 flex-1">
              <Tabs.List aria-label="Desk views" className="w-fit px-2">
                <Tabs.Tab id="ready">
                  <span className="flex items-center gap-1.5"><Target className="size-3.5" aria-hidden /> Ready <CountBadge n={ready.length + openSignals.filter(o => o.state === "valid" && !ready.some(r => r.symbol === o.symbol)).length} /></span>
                  <Tabs.Indicator />
                </Tabs.Tab>
                <Tabs.Tab id="watching">
                  <span className="flex items-center gap-1.5"><Radar className="size-3.5" aria-hidden /> Watching <CountBadge n={watching.length} /></span>
                  <Tabs.Indicator />
                </Tabs.Tab>
                <Tabs.Tab id="levels">
                  <span className="flex items-center gap-1.5">Intraday levels <span className="text-muted">{chartSymbol.replace("USDT", "")}</span></span>
                  <Tabs.Indicator />
                </Tabs.Tab>
              </Tabs.List>
            </Tabs.ListContainer>
            <Button size="sm" variant="ghost" onPress={toggleHow} aria-expanded={showHow} aria-label="How an entry happens">
              {/* A phone has room for the tabs or this label, not both. */}
              <Info className="size-3.5" aria-hidden /> <span className="hidden sm:inline">How an entry happens</span><span className="sm:hidden">How</span>
              {showHow ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
            </Button>
          </div>

          {showHow && (
            <div className="grid grid-cols-1 border-b border-separator sm:grid-cols-2 xl:grid-cols-4">
              <Step n={1} title="4h bias" body="Which way is the bigger trend? We only trade in that direction. Bearish 4h → shorts only." />
              <Step n={2} title="1h pullback to value" body="Wait for price to retrace into the EMA20/50 zone without breaking the last swing. That's the entry zone." />
              <Step n={3} title="Trigger candle closes" body="A 1h candle must CLOSE as a hammer, engulfing, or above the previous high. No close, no trade." />
              <Step n={4} title="Plan & size" body="Stop behind the swing, TP1 at the next level (≥1.5R), size so a stop-out costs your risk per trade (0.5% of the account by default)." />
            </div>
          )}

          <Tabs.Panel id="ready" className="space-y-3 p-3">
            {openSignals.length > 0 && <SentSignals signals={openSignals} />}
            {firstLoad ? (
              <div className="space-y-3" aria-busy="true" aria-label="Scanning">
                <Skeleton className="h-40 rounded-lg" />
              </div>
            ) : ready.length === 0 ? (
              <EmptyState
                icon={Target}
                title="No setup has fully triggered on the last 1h close."
                description="That's normal: the engine fires only a few trades a week across today's coins. Check Watching to see what each coin is waiting for."
                action={<Button size="sm" variant="secondary" onPress={() => setTab("watching")}>See today's coins</Button>}
              />
            ) : (
              <div className="space-y-3">
                {ready.map(row => (
                  <ReadyCard key={row.symbol} row={row} onLog={() => logTrade(row)} logging={logging === row.symbol} />
                ))}
              </div>
            )}
          </Tabs.Panel>

          <Tabs.Panel id="watching">
            <WatchTable
              rows={watching}
              expanded={expanded}
              onToggle={sym => setExpanded(expanded === sym ? null : sym)}
              loading={firstLoad}
              coinCount={coins.length}
            />
          </Tabs.Panel>

          <Tabs.Panel id="levels">
            <IntradayLevels symbol={chartSymbol} state={intraday} />
          </Tabs.Panel>
        </Tabs>
      </Panel>

      {(toast || ticketToast) && (
        <div className="toast-dock md:w-72" role="status">
          <Alert status={ticketToast || toast?.ok ? "success" : "danger"}>
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{ticketToast ?? toast?.msg}</Alert.Title>
            </Alert.Content>
          </Alert>
        </div>
      )}
    </div>
  );
}

// ─── small pieces ────────────────────────────────────────────────────────────

function HeaderStat({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col justify-center gap-0.5" title={hint}>
      <span className="text-2xs font-medium uppercase tracking-wide text-muted">{label}</span>
      <div className="flex min-h-6 items-center">{children}</div>
    </div>
  );
}

/** Bias / trend / macro word: green when it favours longs, red when it favours shorts. */
function BiasText({ value, up, down }: { value: string; up: string; down: string }) {
  const v = value.toUpperCase();
  const tone = v === up.toUpperCase() ? "text-profit" : v === down.toUpperCase() ? "text-loss" : "text-foreground";
  return <span className={cn("text-sm font-medium capitalize", tone)}>{value.toLowerCase()}</span>;
}

function CountBadge({ n }: { n: number }) {
  return <span className="num rounded-full bg-default px-1.5 text-3xs text-muted">{n}</span>;
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="border-separator px-4 py-3 [&:not(:last-child)]:border-b sm:[&:not(:last-child)]:border-r xl:[&:not(:last-child)]:border-b-0">
      <div className="mb-1 flex items-center gap-2">
        <span className="num flex size-5 items-center justify-center rounded-full bg-accent-soft text-3xs font-semibold text-accent-soft-foreground">{n}</span>
        <span className="text-xs font-semibold">{title}</span>
      </div>
      <p className="text-xs leading-relaxed text-muted">{body}</p>
    </div>
  );
}


function BiasArrow({ dir }: { dir: "bullish" | "bearish" | "neutral" }) {
  if (dir === "bullish") return <span className="inline-flex items-center gap-1 text-xs text-profit"><TrendingUp className="size-3.5" /> 4h up</span>;
  if (dir === "bearish") return <span className="inline-flex items-center gap-1 text-xs text-loss"><TrendingDown className="size-3.5" /> 4h down</span>;
  return <span className="text-xs text-muted">4h flat</span>;
}


const TH = "px-3 py-2 text-2xs font-medium uppercase tracking-wide";
const TD = "px-3 py-2";

// ─── Desk symbol list (left column: today's coins, busiest first) ───────────

// ─── Signals already sent to Telegram (one source of truth with the bot) ────

function SentSignals({ signals }: { signals: OpenSignal[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="border-b border-separator bg-surface-secondary px-3 py-2 text-2xs font-medium uppercase tracking-wide text-muted">
        Sent to Telegram, still open
      </div>
      <ul className="divide-y divide-separator">
        {signals.map(o => (
          <li key={o.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-xs">
            <span className="w-14 font-semibold">{o.symbol.replace("USDT", "")}</span>
            <DirectionChip direction={o.action} />
            {o.againstBtc && <AgainstBtcChip action={o.action} />}
            <Chip size="sm" variant="soft" color={OPEN_SIGNAL_META[o.state].color} className="font-semibold">{OPEN_SIGNAL_META[o.state].label}</Chip>
            <span className="text-muted">{ago(o.sentAt)}</span>
            <span className="num">entry ${fmt(o.plan.entry)}</span>
            <span className="num text-loss">SL ${fmt(o.plan.stopLoss)}</span>
            <span className="num text-profit">TP1 ${fmt(o.plan.tp1)}</span>
            <span className="num">now ${fmt(o.price)}</span>
            <span className="min-w-0 flex-1 text-right text-foreground/80">{o.headline}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Ready card: the full trade plan ─────────────────────────────────────────

function ReadyCard({ row, onLog, logging }: { row: DeskRow; onLog: () => void; logging: boolean }) {
  const s = row.signal!;
  const p = s.plan;
  const isLong = s.action === "LONG";
  const steps = isLong
    ? [
        `Enter LONG at market (~$${fmt(p.entry)}). If price already ran > 0.3 ATR past the trigger close, skip it — don't chase.`,
        `Place the stop-loss at $${fmt(p.stopLoss)} (${pct(p.stopPct)}) immediately. This is the thesis-invalidation level.`,
        `Take ~50% off at TP1 $${fmt(p.tp1)} (${pct(p.tp1Pct)}) and move the stop to break-even.`,
        `Let the rest run to TP2 $${fmt(p.tp2)} (${pct(p.tp2Pct)}).`,
        `Time stop: if neither TP1 nor SL is hit within ${p.timeStopHours}h, close the trade. A setup that doesn't move was wrong.`,
      ]
    : [
        `Enter SHORT at market (~$${fmt(p.entry)}). If price already fell > 0.3 ATR past the trigger close, skip it — don't chase.`,
        `Place the stop-loss at $${fmt(p.stopLoss)} (${pct(p.stopPct)}) immediately. This is the thesis-invalidation level.`,
        `Cover ~50% at TP1 $${fmt(p.tp1)} (${pct(p.tp1Pct)}) and move the stop to break-even.`,
        `Let the rest run to TP2 $${fmt(p.tp2)} (${pct(p.tp2Pct)}).`,
        `Time stop: if neither TP1 nor SL is hit within ${p.timeStopHours}h, close the trade. A setup that doesn't move was wrong.`,
      ];

  return (
    <div className={cn("overflow-hidden rounded-lg border bg-surface", isLong ? "border-profit/40" : "border-loss/40")}>
      <div className="flex flex-wrap items-center gap-2 border-b border-separator px-4 py-2.5">
        <span className="text-base font-semibold">{row.symbol.replace("USDT", "")}</span>
        <DirectionChip direction={s.action} />
        {s.againstBtc && <AgainstBtcChip action={s.action} />}
        <Chip size="sm" variant="soft" color="accent" className="capitalize">{archetypeLabel(s.setupArchetype)}</Chip>
        <Chip size="sm" variant="soft">Grade {s.setupQuality}</Chip>
        <span className="text-2xs text-muted">trigger: {s.entryTrigger.replace(/_/g, " ")}</span>
        <span className="ml-auto text-xs text-muted">
          score <span className="num font-semibold text-foreground">{s.score}</span>
          {s.totalTrades > 0 && <> · <span className="num">{s.winRate}%</span> WR (n=<span className="num">{s.totalTrades}</span>)</>}
        </span>
      </div>

      <p className="px-4 pt-3 text-[13px] text-foreground/90">{s.thesis}</p>

      <div className="grid grid-cols-1 gap-4 px-4 py-3 md:grid-cols-[200px_1fr]">
        <PriceLadder isLong={isLong} p={p} />

        <div>
          <div className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-muted">Execution</div>
          <ol className="space-y-1.5">
            {steps.map((t, i) => (
              <li key={i} className="flex gap-2 text-xs leading-relaxed text-foreground/90">
                <span className="num mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-default text-3xs font-semibold">{i + 1}</span>
                <span>{t}</span>
              </li>
            ))}
          </ol>

          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Mini label="Position" value={`${fmt(p.position.units)} ${row.symbol.replace("USDT", "")}`} sub={`≈ $${p.position.notional.toLocaleString()}`} />
            <Mini label="Risk if stopped" value={`$${p.position.riskAmount.toFixed(2)}`} sub={`${s.againstBtc ? "half risk, against BTC: " : ""}${p.position.riskPercent}% of account${p.position.drawdownMultiplier < 1 ? ` × ${p.position.drawdownMultiplier} DD throttle` : ""}`} tone={s.againstBtc ? "warning" : undefined} />
            <Mini label="Reward : Risk" value={`${p.riskReward.toFixed(1)} : 1`} sub="to TP1" tone={p.riskReward >= 2 ? "profit" : "warning"} />
            <Mini label="Fees + slippage" value={`${p.costR.toFixed(2)} R`} sub="of your risk unit" tone={p.costR <= 0.15 ? "profit" : "warning"} />
          </div>

          {s.warnings.length > 0 && (
            <Alert status="warning" className="mt-3">
              <Alert.Indicator>
                <ShieldAlert className="size-4" />
              </Alert.Indicator>
              <Alert.Content>
                <Alert.Description>
                  <ul className="space-y-0.5 text-xs">{s.warnings.slice(0, 3).map((w, i) => <li key={i}>{w}</li>)}</ul>
                </Alert.Description>
              </Alert.Content>
            </Alert>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button size="sm" variant="secondary" onPress={onLog} isDisabled={logging} isPending={logging}>
              <Target className="size-3.5" /> {logging ? "Logging…" : "I took this trade — log it"}
            </Button>
            <details className="text-xs text-muted">
              <summary className="cursor-pointer hover:text-foreground">Why this fired</summary>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">{s.reasons.slice(0, 8).map((r, i) => <li key={i}>{r}</li>)}</ul>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}



// ─── Watching: the stalking checklist as a table with expandable rows ───────

function WatchTable({
  rows, expanded, onToggle, loading, coinCount,
}: {
  rows: DeskRow[]; expanded: string | null; onToggle: (symbol: string) => void; loading: boolean; coinCount: number;
}) {
  if (loading) {
    return (
      <div className="space-y-2 p-4" aria-busy="true" aria-label={`Scanning ${coinCount} coins on 1h + 4h`}>
        <p className="text-xs text-muted">
          {coinCount > 0 ? `Checking today's ${coinCount} coins on the 1h and 4h charts…` : "Picking today's busiest coins for day trading…"}
        </p>
        {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-9 rounded-lg" />)}
      </div>
    );
  }
  if (rows.length === 0) {
    return <EmptyState icon={Radar} title="No coins checked yet." description="Today's coins appear here after the first scan." />;
  }

  // Flatten: each coin row, followed by its detail row when expanded.
  type Item = { kind: "row"; id: string; row: DeskRow } | { kind: "detail"; id: string; row: DeskRow };
  const items: Item[] = [];
  for (const r of rows) {
    items.push({ kind: "row", id: r.symbol, row: r });
    if (expanded === r.symbol) items.push({ kind: "detail", id: `${r.symbol}__detail`, row: r });
  }

  return (
    <Table variant="secondary" className="rounded-none">
      <Table.ScrollContainer className="scrollbar-thin">
        <Table.Content
          aria-label="Watching — what each coin is waiting for"
          className="min-w-[860px] text-[13px]"
          onRowAction={key => {
            const k = String(key);
            if (!k.endsWith("__detail")) onToggle(k);
          }}
        >
          <Table.Header className="sticky top-0 z-10">
            <Table.Column isRowHeader className={TH}>Symbol</Table.Column>
            <Table.Column className={TH}>Bias</Table.Column>
            <Table.Column className={TH}>Stage</Table.Column>
            <Table.Column className={TH}>Looking for</Table.Column>
            <Table.Column className={cn(TH, "w-40")}>Rules passed</Table.Column>
            <Table.Column className={TH}>What to do</Table.Column>
            <Table.Column className={cn(TH, "w-8")}><span className="sr-only">Expand</span></Table.Column>
          </Table.Header>
          <Table.Body items={items}>
            {(item: Item) => item.kind === "row"
              ? <WatchRowCells key={item.id} row={item.row} expanded={expanded === item.row.symbol} />
              : (
                <Table.Row id={item.id} key={item.id} className="[&>td]:bg-surface-secondary">
                  <Table.Cell colSpan={7} className="px-4 pb-4 pt-2">
                    <WatchDetail row={item.row} />
                  </Table.Cell>
                </Table.Row>
              )}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  );
}

function WatchRowCells({ row, expanded }: { row: DeskRow; expanded: boolean }) {
  const c = row.checklist;
  const { met, total } = ruleCounts(row);
  const progress = (met / total) * 100;
  const isLong = c.direction === "LONG";
  const v = row.verdict;
  return (
    <Table.Row id={row.symbol} textValue={row.symbol} className="cursor-pointer">
      <Table.Cell className={TD}>
        <div className="font-semibold">{row.symbol.replace("USDT", "")}</div>
        <div className="num text-2xs text-muted">${fmt(row.price)}</div>
      </Table.Cell>
      <Table.Cell className={TD}><BiasArrow dir={row.htfBias.direction} /></Table.Cell>
      <Table.Cell className={TD}><StageChip stage={v.stage} /></Table.Cell>
      <Table.Cell className={cn(TD, "text-xs capitalize", isLong ? "text-profit" : "text-loss")}>
        {archetypeLabel(c.archetype)} {c.direction.toLowerCase()}
      </Table.Cell>
      <Table.Cell className={TD}>
        <ProgressBar
          aria-label={`${met} of ${total} rules passed`}
          value={Math.max(4, progress)}
          size="sm"
          color={STAGE_META[v.stage].color}
          className="flex-row items-center gap-2"
        >
          <ProgressBar.Track className="flex-1">
            <ProgressBar.Fill />
          </ProgressBar.Track>
          <span className="num w-8 text-right text-2xs text-muted">{met}/{total}</span>
        </ProgressBar>
      </Table.Cell>
      <Table.Cell className={cn(TD, "max-w-[380px] text-xs text-muted")}>
        <span className={cn("font-medium", VERDICT_TONE[v.status])}>{v.headline}</span>
      </Table.Cell>
      <Table.Cell className={cn(TD, "text-muted")}>
        {expanded ? <ChevronUp className="size-4" aria-label="Collapse" /> : <ChevronDown className="size-4" aria-label="Expand" />}
      </Table.Cell>
    </Table.Row>
  );
}

function WatchDetail({ row }: { row: DeskRow }) {
  const c = row.checklist;
  const v = row.verdict;
  return (
    <div className="space-y-4">
      <VerdictBox verdict={v} />
      <ClosePreviewFor symbol={row.symbol} armed={v.stage === "armed"} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_260px]">
      <div>
        <div className="mb-2 text-2xs font-medium uppercase tracking-wide text-muted">
          The rules for a {archetypeLabel(c.archetype)} {c.direction === "LONG" ? "buy (long)" : "sell (short)"}
        </div>
        <ul className="space-y-2.5">
          {c.items.map(it => (
            <li key={it.key} className="flex gap-2">
              <StatusIcon status={it.status} warn={it.warn} />
              <div className="min-w-0 whitespace-normal">
                <div className={cn("text-xs font-medium", it.warn ? "text-warning" : it.status === "met" ? "text-foreground" : it.status === "failed" ? "text-loss" : "text-warning")}>
                  {it.label}
                  <RuleHelp ruleKey={it.key} label={it.label} />
                  {it.hard && <span className="ml-1.5 text-3xs uppercase tracking-wide text-muted">required</span>}
                </div>
                {it.plain && <div className="text-2xs leading-relaxed text-foreground/75">{it.plain}</div>}
                <div className="num text-2xs text-muted">Now: {it.detail}</div>
              </div>
            </li>
          ))}
          {v.engineCheck && (
            <li className="flex gap-2 rounded-lg bg-danger-soft p-2">
              <StatusIcon status="failed" />
              <div className="min-w-0 whitespace-normal">
                <div className="text-xs font-medium text-loss">
                  {v.engineCheck.label}
                  <span className="ml-1.5 text-3xs uppercase tracking-wide text-muted">engine</span>
                </div>
                <div className="text-2xs leading-relaxed text-foreground/75">{v.engineCheck.plain}</div>
                <div className="num text-2xs text-muted">Now: {v.engineCheck.detail}</div>
              </div>
            </li>
          )}
        </ul>
      </div>

      <div className="space-y-1.5">
        <div className="text-2xs font-medium uppercase tracking-wide text-muted">Levels now</div>
        <KV k="Price" v={`$${fmt(c.levels.price)}`} />
        <KV k="EMA20 / EMA50" v={`$${fmt(c.levels.ema20)} / $${fmt(c.levels.ema50)}`} />
        {c.levels.zoneLow != null && <KV k={c.archetype === "trend_pullback" ? "Pullback zone" : "Fade zone"} v={`$${fmt(c.levels.zoneLow)} – $${fmt(c.levels.zoneHigh)}`} />}
        {c.levels.rangeLow != null && <KV k="Range" v={`$${fmt(c.levels.rangeLow)} – $${fmt(c.levels.rangeHigh)}`} />}
        {c.levels.invalidation != null && <KV k="Stop would be" v={`$${fmt(c.levels.invalidation)}`} tone="loss" />}
        <KV k="ATR (1h)" v={`$${fmt(c.levels.atr)}`} />
        <KV k="RSI" v={c.levels.rsi.toFixed(0)} />
        <KV k="Last-bar volume" v={`${c.levels.volumeRatio.toFixed(2)}× avg`} />
        {row.rejection && (
          <div className="border-t border-separator pt-1 text-2xs text-muted" title={row.rejection.detail}>
            Engine code: {row.rejection.reason}
            {row.rejection.direction && <> (checked {row.rejection.direction})</>}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}


function KV({ k, v, tone }: { k: string; v: string; tone?: "loss" }) {
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-muted">{k}</span>
      <span className={cn("num", tone === "loss" ? "text-loss" : "text-foreground")}>{v}</span>
    </div>
  );
}
